import React from 'react';
import './topTabs.scss';

export interface TopTab<T extends string> {
  id: T;
  label: string;
  hint?: string;  // optional sub-label; omit for the compact variant
  icon?: string;  // FontAwesome class, e.g. "fa-folder-tree"
}

interface TopTabsProps<T extends string> {
  tabs: TopTab<T>[];
  active: T;
  onChange: (id: T) => void;
  ariaLabel?: string;
  /** When true, render the compact form (no hint sub-label) even if a hint
   *  is provided. Useful for sub-tab strips inside panels. */
  compact?: boolean;
  className?: string;
  /** In-app help anchor for the strip, e.g. 'reports.tabs'. */
  dataHelp?: string;
}

function TopTabs<T extends string>({
  tabs,
  active,
  onChange,
  ariaLabel,
  compact = false,
  className = '',
  dataHelp,
}: TopTabsProps<T>) {
  return (
    <div
      className={`top-tabs__bar ${compact ? 'top-tabs__bar--compact' : ''} ${className}`}
      data-help={dataHelp}
      role="tablist"
      aria-label={ariaLabel}
    >
      {tabs.map(t => {
        const isActive = t.id === active;
        const showHint = !compact && !!t.hint;
        return (
          <button
            key={t.id}
            role="tab"
            type="button"
            aria-selected={isActive}
            className={[
              'top-tab',
              isActive ? 'top-tab--active' : '',
              showHint ? 'top-tab--with-hint' : 'top-tab--compact',
            ].filter(Boolean).join(' ')}
            onClick={() => onChange(t.id)}
          >
            {t.icon && <i className={`fas ${t.icon} top-tab__icon`} aria-hidden="true" />}
            <span className="top-tab__label">{t.label}</span>
            {showHint && <span className="top-tab__hint">{t.hint}</span>}
          </button>
        );
      })}
    </div>
  );
}

export default TopTabs;
